<template>
  <!-- Действия с маршрутом - внутри самой карточки (а не в нижнем баре):
       таб-бар всегда остаётся вкладками. GPX - главное действие. -->
  <div class="flex items-stretch justify-around h-[60px]" role="group" :aria-label="$t('routeDetails.actionsLabel')">
    <button type="button" class="action-btn" :class="saved && 'text-primary'" :aria-pressed="saved" @click="$emit('save')">
      <span class="pill" :class="saved && 'bg-primary/10'"><Bookmark class="w-[22px] h-[22px]" :class="saved && 'fill-current'" aria-hidden="true" /></span>
      <span class="sr-only">{{ saved ? $t('routeDetails.actions.saved') : $t('routeDetails.actions.save') }}</span>
    </button>
    <div ref="shareRoot" class="relative flex flex-1 min-w-0">
      <button type="button" class="action-btn w-full" :aria-haspopup="hasStart ? 'menu' : undefined" :aria-expanded="hasStart ? menuOpen : undefined" @click="onShare">
        <span class="pill" :class="menuOpen && 'bg-primary/10'"><Share2 class="w-[22px] h-[22px]" aria-hidden="true" /></span><span class="sr-only">{{ $t('routeDetails.actions.share') }}</span>
      </button>
      <!-- Подменю «Поделиться»: ссылка, координаты старта, 2ГИС, Google Карты. -->
      <div
        v-if="menuOpen"
        role="menu"
        data-share-menu
        class="absolute left-0 z-50 w-64 rounded-xl border border-border bg-card p-1.5 text-card-foreground shadow-ink-lg"
        :class="menuDown ? 'top-full mt-2' : 'bottom-full mb-2'"
        @keydown.esc.stop.prevent="closeMenu"
      >
        <button v-for="item in menuItems" :key="item.key" type="button" role="menuitem" :data-share-item="item.key" class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[14px] font-medium hover:bg-accent" @click="pick(item.key)">
          <component :is="item.icon" class="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />{{ item.label }}
        </button>
      </div>
    </div>
    <button type="button" class="action-btn" @click="$emit('register')">
      <span class="pill"><ShieldCheck class="w-[22px] h-[22px]" aria-hidden="true" /></span><span class="sr-only">{{ $t('routeDetails.actions.registerFull') }}</span>
    </button>
    <button type="button" class="action-btn text-primary" :disabled="!canDownload" @click="$emit('download')">
      <span class="pill bg-primary/10"><Download class="w-[22px] h-[22px]" aria-hidden="true" /></span><span class="sr-only">GPX</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Bookmark, Copy, Download, Link, MapPin, Navigation, Share2, ShieldCheck } from "lucide-vue-next";

// hasStart - у маршрута есть точка старта: «Поделиться» открывает подменю
// (ссылка, координаты, 2ГИС, Google Карты); без неё - сразу «ссылка».
// menuDown - подменю раскрывается вниз (панель в шапке десктопа), иначе вверх.
const props = defineProps<{ saved: boolean; canDownload: boolean; hasStart?: boolean; menuDown?: boolean }>();
const emit = defineEmits<{ (e: "save" | "share" | "register" | "download" | "copy-start" | "open-2gis" | "open-google-maps"): void }>();

const { t } = useI18n();
const menuOpen = ref(false);
const shareRoot = ref<HTMLElement | null>(null);
const closeMenu = () => (menuOpen.value = false);
const onShare = () => {
  if (!props.hasStart) emit("share");
  else menuOpen.value = !menuOpen.value;
};
const menuItems = computed(() => [
  { key: "share" as const, label: t("routeDetails.shareMenu.link"), icon: Link },
  { key: "copy-start" as const, label: t("routeDetails.gettingThere.copy"), icon: Copy },
  { key: "open-2gis" as const, label: t("routeDetails.gettingThere.open2gis"), icon: Navigation },
  { key: "open-google-maps" as const, label: t("routeDetails.gettingThere.openGoogleMaps"), icon: MapPin },
]);
const pick = (key: "share" | "copy-start" | "open-2gis" | "open-google-maps") => {
  closeMenu();
  emit(key);
};
const onDocPointerDown = (e: PointerEvent) => {
  if (menuOpen.value && shareRoot.value && !shareRoot.value.contains(e.target as Node)) closeMenu();
};
onMounted(() => document.addEventListener("pointerdown", onDocPointerDown));
onBeforeUnmount(() => document.removeEventListener("pointerdown", onDocPointerDown));
</script>

<style scoped>
/* Как AppTabBar: плоские кнопки «значок в пилюле + подпись», без рамок. */
.action-btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  font-size: 11px;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
}
.pill {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 2.25rem;
  border-radius: 9999px;
  transition: background-color 0.15s;
}
.action-btn:disabled {
  opacity: 0.45;
}
</style>
